import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authAPI, requestOTP, verifyOTP, checkPhone, getPublicSettings } from '../api/auth';
import { sanitizePhoneInput, toEnglishDigits } from '../utils/phone';
import '../styles/auth.css';

const hostname = window.location.hostname;
const IS_PERSIAN =
  hostname === 'potterhead.ir' ||
  hostname === 'www.potterhead.ir' ||
  (hostname === 'localhost' && !window.location.pathname.startsWith('/en'));

const T = IS_PERSIAN
  ? {
      title: 'دروازه‌ی محرمانه',
      subtitle: '⚡ هاگوارتز منتظر شماست ⚡',
      tabUser: '🧙 کاربر',
      tabAdmin: '🔐 ادمین',
      labelPhone: '📱 شماره تماس',
      phonePlaceholder: '09xxxxxxxxx',
      labelInvite: '🎟 کد دعوت (در صورت داشتن)',
      labelInviteRequired: '🎟 کد دعوت (الزامی برای کاربران جدید)',
      invitePlaceholder: 'کد دعوت خود را وارد کنید',
      waitlistBanner: 'لیست انتظار فعال است. اعضای قبلی فقط با شماره‌شان وارد می‌شوند؛ اگر دعوت‌نامه دارید، شماره و کد دعوت خود را وارد کنید.',
      inviteRequiredError: 'این شماره هنوز عضو سایت نیست. برای ورود، کد دعوت خود را نیز وارد کنید.',
      sendCode: '🚂 ارسال کد ورود',
      sending: '✨ در حال ارسال...',
      labelOtp: '🔑 کد تأیید',
      otpHint: 'کد ۶ رقمی پیامک‌شده را وارد کنید',
      verify: '⚡ ورود به هاگوارتز',
      verifying: '✨ در حال تأیید...',
      changePhone: '← تغییر شماره',
      otpExpiry: '⏱ کد برای ۱۰ دقیقه معتبر است',
      attemptsLeft: (n) => `🔢 تلاش‌های باقی‌مانده: ${n}`,
      labelUsername: '👤 نام کاربری یا ایمیل',
      labelPassword: '🔒 رمز عبور',
      adminLogin: '🗝 ورود ادمین',
      adminLogging: '✨ در حال ورود...',
      watermark: '— Platform 9¾ —',
    }
  : {
      title: 'Secret Gateway',
      subtitle: '⚡ Hogwarts awaits you ⚡',
      tabUser: '🧙 User',
      tabAdmin: '🔐 Admin',
      labelPhone: '📱 Phone Number',
      phonePlaceholder: '09xxxxxxxxx',
      labelInvite: '🎟 Invite Code (if you have one)',
      labelInviteRequired: '🎟 Invite Code (required for new users)',
      invitePlaceholder: 'Enter your invite code',
      waitlistBanner: 'The waitlist is currently active. Existing members can log in with just their number; if you have an invitation, enter your phone number and invite code.',
      inviteRequiredError: 'This number is not registered yet. Please enter your invite code to continue.',
      sendCode: '🚂 Send OTP',
      sending: '✨ Sending...',
      labelOtp: '🔑 Verification Code',
      otpHint: 'Enter the 6-digit code sent to your phone',
      verify: '⚡ Enter Hogwarts',
      verifying: '✨ Verifying...',
      changePhone: '← Change Number',
      otpExpiry: '⏱ Code is valid for 10 minutes',
      attemptsLeft: (n) => `🔢 Attempts remaining: ${n}`,
      labelUsername: '👤 Username or Email',
      labelPassword: '🔒 Password',
      adminLogin: '🗝 Admin Login',
      adminLogging: '✨ Logging in...',
      watermark: '— Platform 9¾ —',
    };

/* ── Static decoration data (computed once) ── */
const SPARKS = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: `${5 + ((i * 37 + 13) % 90)}%`,
  bottom: `${5 + ((i * 53 + 7) % 55)}%`,
  size: 2 + (i % 3),
  dur: `${3.5 + (i % 5)}s`,
  delay: `${(i * 0.45) % 4}s`,
  color: i % 4 === 0 ? '#8be8f7' : i % 3 === 0 ? '#f7d98b' : '#d3a625',
}));

const STEAM = [
  { left: '8%',  dur: '7s',   delay: '0s'   },
  { left: '22%', dur: '9s',   delay: '1.8s' },
  { left: '38%', dur: '6.5s', delay: '0.5s' },
  { left: '55%', dur: '8s',   delay: '2.5s' },
  { left: '70%', dur: '7.5s', delay: '1s'   },
  { left: '84%', dur: '10s',  delay: '3.2s' },
];

const SLEEPERS = [4, 18, 32, 46];

export default function HiddenLogin() {
  const [mode, setMode] = useState('user');
  const [step, setStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [otp, setOtp] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  // این مقدار مستقیماً از تنظیمات ادمین (تب «تنظیمات ثبت‌نام») خوانده می‌شود —
  // کلید «الزام کد دعوت در صفحه ورود». پیش‌فرض روشن است.
  const [waitlistActive, setWaitlistActive] = useState(false);

  useEffect(() => {
    getPublicSettings()
      .then((res) => {
        if (res.data?.success) {
          setWaitlistActive(res.data.settings?.requireInviteCodeOnLogin !== false);
        }
      })
      .catch(() => {});
  }, []);

  const resetState = (nextMode) => {
    setMode(nextMode); setStep('phone');
    setError(''); setMessage(''); setOtp(''); setPassword(''); setInviteCode('');
  };

  const requestUserOtp = async (e) => {
    e.preventDefault(); setError(''); setMessage(''); setLoading(true);
    try {
      // طبق تنظیم «الزام کد دعوت در صفحه ورود» در پنل ادمین: فقط اعضای قبلی
      // می‌توانند بدون کد دعوت وارد شوند — کاربر جدید باید حتماً کد دعوت خودش
      // را هم وارد کند.
      if (waitlistActive) {
        const checkRes = await checkPhone(phoneNumber);
        const exists = checkRes.data?.exists;
        if (!exists && !inviteCode.trim()) {
          setError(T.inviteRequiredError);
          return;
        }
      }

      const res = await requestOTP(phoneNumber, inviteCode.trim());
      if (res.data.success) { setMessage(res.data.message || 'کد ورود ارسال شد'); setStep('otp'); }
      else setError(res.data.message || 'خطا در ارسال کد');
    } catch (err) { setError(err.response?.data?.message || 'خطا در درخواست کد'); }
    finally { setLoading(false); }
  };

  const verifyUserOtp = async (e) => {
    e.preventDefault(); setError(''); setMessage(''); setLoading(true);
    try {
      const res = await verifyOTP(phoneNumber, otp, inviteCode.trim());
      if (res.data.success) {
        Cookies.remove('admin');
        Cookies.set('token', res.data.token, { expires: 30 });
        window.location.href = '/dashboard';
      } else {
        setError(res.data.message || 'کد نامعتبر است');
        if (res.data.attemptsRemaining !== undefined) setAttemptsRemaining(res.data.attemptsRemaining);
      }
    } catch (err) { setError(err.response?.data?.message || 'خطا در تایید کد'); }
    finally { setLoading(false); }
  };

  const loginAdmin = async (e) => {
    e.preventDefault(); setError(''); setMessage(''); setLoading(true);
    try {
      const res = await authAPI.loginAdmin(username, password);
      if (res.data.success) {
        Cookies.set('token', res.data.token, { expires: 30 });
        Cookies.set('admin', JSON.stringify(res.data.admin), { expires: 30 });
        window.location.href = '/admin/dashboard';
      } else setError(res.data.message || 'ورود ناموفق بود');
    } catch (err) { setError(err.response?.data?.message || 'نام کاربری یا رمز عبور اشتباه است'); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page-root">

      {/* ══ Platform 9¾ brick scene ═══════════════════════ */}
      <div className="platform-scene">
        <div className="platform-atmo" />

        <div className="platform-sign">
          <span>🕰</span>
          <span>سکوی</span>
          <strong>9¾</strong>
          <span>—</span>
          <span>هاگوارتز اکسپرس</span>
        </div>

        <div className="sparks-layer" aria-hidden="true">
          {SPARKS.map(s => (
            <span key={s.id} className="spark" style={{
              left: s.left, bottom: s.bottom,
              width: s.size + 'px', height: s.size + 'px',
              '--c': s.color, '--dur': s.dur, '--delay': s.delay,
            }} />
          ))}
        </div>

        <div className="steam-layer" aria-hidden="true">
          {STEAM.map((s, i) => (
            <div key={i} className="steam-puff" style={{
              left: s.left, '--dur': s.dur, '--delay': s.delay,
            }} />
          ))}
        </div>

        <div className="tracks" aria-hidden="true">
          {SLEEPERS.map(b => (
            <div key={b} className="sleeper" style={{ '--b': `${b}px` }} />
          ))}
        </div>
      </div>

      {/* ══ Card ══════════════════════════════════════════ */}
      <div className="auth-scroll">
        <div className="auth-card-magic">

          <div className="auth-header">
            <span className="auth-crest">🦉</span>
            <h1 className="auth-title">{T.title}</h1>
            <p className="auth-subtitle">{T.subtitle}</p>
          </div>

          <div className="auth-switch" role="tablist">
            <button type="button" className={'auth-switch-btn' + (mode === 'user' ? ' active' : '')} onClick={() => resetState('user')} disabled={loading}>
              {T.tabUser}
            </button>
            <button type="button" className={'auth-switch-btn' + (mode === 'admin' ? ' active' : '')} onClick={() => resetState('admin')} disabled={loading}>
              {T.tabAdmin}
            </button>
          </div>

          {mode === 'user' && (
            <div className="auth-step-ind" aria-hidden="true">
              <div className={'auth-step-dot' + (step === 'phone' ? ' active' : '')} />
              <div className={'auth-step-dot' + (step === 'otp' ? ' active' : '')} />
            </div>
          )}

          {error   && <div className="auth-alert auth-alert-error">⚠️ {error}</div>}
          {message && <div className="auth-alert auth-alert-success">✅ {message}</div>}

          {mode === 'user' && step === 'phone' && waitlistActive && !error && !message && (
            <div className="auth-alert auth-alert-info">🎟 {T.waitlistBanner}</div>
          )}

          {mode === 'user' && step === 'phone' && (
            <form className="auth-form" onSubmit={requestUserOtp}>
              <div className="auth-field">
                <label className="auth-label">{T.labelPhone}</label>
                <input className="auth-input" type="tel" placeholder={T.phonePlaceholder}
                  value={phoneNumber} onChange={(e) => setPhoneNumber(sanitizePhoneInput(e.target.value))}
                  required disabled={loading} autoComplete="tel" />
              </div>
              <div className="auth-field">
                <label className="auth-label">{waitlistActive ? T.labelInviteRequired : T.labelInvite}</label>
                <input className="auth-input" type="text" placeholder={T.invitePlaceholder}
                  value={inviteCode} onChange={(e) => setInviteCode(e.target.value)}
                  disabled={loading} autoComplete="off" style={{ textTransform: 'uppercase' }} />
              </div>
              <button className="auth-btn" type="submit" disabled={loading}>
                {loading ? T.sending : T.sendCode}
              </button>
            </form>
          )}

          {mode === 'user' && step === 'otp' && (
            <form className="auth-form" onSubmit={verifyUserOtp}>
              <div className="auth-field">
                <label className="auth-label">{T.labelOtp}</label>
                <input className="auth-input" type="text" inputMode="numeric" placeholder="• • • • • •"
                  value={otp} onChange={(e) => setOtp(toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, 6))}
                  maxLength="6" required disabled={loading} autoComplete="one-time-code"
                  style={{ letterSpacing: '6px', fontSize: '1.2rem' }} />
                <span className="auth-hint">{T.otpHint}</span>
              </div>
              <button className="auth-btn" type="submit" disabled={loading}>
                {loading ? T.verifying : T.verify}
              </button>
              <button type="button" className="auth-btn auth-btn-secondary" onClick={() => setStep('phone')} disabled={loading}>
                {T.changePhone}
              </button>
              <div className="auth-info">
                <p>{T.otpExpiry}</p>
                <p>{T.attemptsLeft(attemptsRemaining)}</p>
              </div>
            </form>
          )}

          {mode === 'admin' && (
            <form className="auth-form" onSubmit={loginAdmin}>
              <div className="auth-field">
                <label className="auth-label">{T.labelUsername}</label>
                <input className="auth-input" type="text" placeholder="admin@example.com"
                  value={username} onChange={(e) => setUsername(e.target.value)}
                  required disabled={loading} autoComplete="username" style={{ textAlign: 'left' }} />
              </div>
              <div className="auth-field">
                <label className="auth-label">{T.labelPassword}</label>
                <input className="auth-input" type="password" placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required disabled={loading} autoComplete="current-password" />
              </div>
              <button className="auth-btn" type="submit" disabled={loading}>
                {loading ? T.adminLogging : T.adminLogin}
              </button>
            </form>
          )}

          <div className="auth-platform-watermark">{T.watermark}</div>
        </div>
      </div>
    </div>
  );
}
